// Iter79 — Self-service change-password card for Settings.
import React, { useState } from "react";
import axios from "axios";
import { KeyRound } from "lucide-react";
import { useStore } from "../store";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ChangePasswordCard() {
  const addToast = useStore((s) => s.addToast);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setError(""); };

  const submit = async () => {
    setError("");
    if (!current) { setError("Current password is required"); return; }
    if (next.length < 8) { setError("New password must be at least 8 characters"); return; }
    if (next !== confirm) { setError("New password and confirmation do not match"); return; }
    if (next === current) { setError("New password must be different from the current one"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      addToast({ type: "success", title: "Password updated" });
      reset(); setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="change-password-card" className="mb-6">
      <div className="rounded-md p-5" style={{ background: "var(--bg-surface, #111420)", border: "1px solid rgba(255,255,255,0.12)" }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-md flex items-center justify-center"
                 style={{ width: 38, height: 38, background: "rgba(24,95,165,0.16)", border: "1px solid rgba(24,95,165,0.45)", color: "#9ec3ec" }}>
              <KeyRound size={18} />
            </div>
            <div>
              <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
                Change password
              </div>
              <div className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9aa1b3" }}>
                Rotate your account password (≥8 characters).
              </div>
            </div>
          </div>
          {!open && (
            <button type="button" onClick={() => setOpen(true)}
              className="px-3 py-1.5 rounded"
              style={{ background: "rgba(24,95,165,0.16)", border: "1px solid rgba(24,95,165,0.55)", color: "#9ec3ec",
                       fontFamily: "DM Mono, monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.5px", cursor: "pointer" }}
              data-testid="change-password-open">
              CHANGE
            </button>
          )}
        </div>

        {open && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="change-password-form">
            <Input label="Current password" type="password" value={current} onChange={setCurrent} testId="change-password-current" />
            <Input label="New password" type="password" value={next} onChange={setNext} testId="change-password-new" />
            <Input label="Confirm new password" type="password" value={confirm} onChange={setConfirm} testId="change-password-confirm" />
            {error && (
              <div className="sm:col-span-3 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", fontFamily: "DM Mono, monospace", fontSize: 11 }} data-testid="change-password-error">
                {error}
              </div>
            )}
            <div className="sm:col-span-3 flex justify-end gap-2">
              <button type="button" onClick={() => { setOpen(false); reset(); }}
                className="px-3 py-1.5 rounded"
                style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
                data-testid="change-password-cancel">
                Cancel
              </button>
              <button type="button" onClick={submit} disabled={busy}
                className="px-3 py-1.5 rounded"
                style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1, border: "none" }}
                data-testid="change-password-submit">
                {busy ? "Saving…" : "Update password"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Input = ({ label, type, value, onChange, testId }) => (
  <div>
    <label style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase" }}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded mt-1 outline-none"
      style={{ background: "var(--bg3, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", fontFamily: "'DM Mono', monospace", fontSize: 12 }}
      data-testid={testId} />
  </div>
);
